import { useAutoneTranslation } from '@autone/translations';
import { typedObject, useDraftState } from '@autone/utils';
import { isEqual } from 'lodash-es';
import { type PropsWithChildren, type ReactNode } from 'react';
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';

import { LoadingIcon, NoDataSvg } from '../../../icons';
import { cn } from '../../../utils';
import { VirtualMultiselect } from '../../virtual-multiselect';
import type { CoreGeneralFilterRef } from '../CoreGeneralFilters.types';

interface CoreGeneralFilterProps<Item extends { id: string }> {
  isFetching?: boolean;
  selectedItems: Item[];
  onSelectedItemsChange: (selectedItems: Item[]) => void;
  alwaysCommit?: boolean;
  items: Item[];
  getItemDisabled?: (item: Item) => boolean;
  renderItem: (item: Item) => ReactNode;
  onScrollEnd?: () => void;
  className?: string;
}

const getFrontendFilterOptionId = <Item extends { id: string }>(item: Item) =>
  item.id;

export const CoreGeneralFilter = forwardRef(
  <Item extends { id: string }>(
    {
      isFetching,
      selectedItems,
      onSelectedItemsChange,
      alwaysCommit,
      items,
      getItemDisabled,
      renderItem,
      onScrollEnd,
      className,
    }: CoreGeneralFilterProps<Item>,
    ref: React.ForwardedRef<CoreGeneralFilterRef>,
  ) => {
    const { draftState, setDraftState, commit } = useDraftState(
      selectedItems,
      onSelectedItemsChange,
      { alwaysCommit },
    );

    useImperativeHandle(
      ref,
      () => ({
        commit: () => {
          if (isEqual(draftState, selectedItems)) {
            return;
          }
          commit();
        },
      }),
      [commit, draftState, selectedItems],
    );

    // Build a lookup map from both items and draft selected items so that
    // onValueChange can resolve any id back to its item object.
    const itemsLookupMap = useMemo(() => {
      const itemsMap = typedObject.fromEntries(
        items.map((item) => [getFrontendFilterOptionId(item), item] as const),
      );
      const draftMap = typedObject.fromEntries(
        draftState.map(
          (item) => [getFrontendFilterOptionId(item), item] as const,
        ),
      );
      return typedObject.merge(itemsMap, draftMap);
    }, [items, draftState]);

    const hasNoResults = items.length === 0;

    const value = useMemo(
      () => draftState.map(getFrontendFilterOptionId),
      [draftState],
    );

    const onValueChange = useCallback(
      (value: Item['id'][]) => {
        setDraftState(value.map((id) => itemsLookupMap[id]));
      },
      [setDraftState, itemsLookupMap],
    );

    const multiselectContent = useMemo(() => {
      if (isFetching) {
        return <Loader className={className} />;
      }
      if (hasNoResults) {
        return <NoResultMessage className={className} />;
      }
      return (
        <VirtualMultiselect.Window
          data-testid="core-general-filter-window"
          className={cn('w-full h-full', className)}
          items={items}
          getItemId={getFrontendFilterOptionId}
          getItemDisabled={getItemDisabled}
          renderItem={renderItem}
          onScrollEnd={onScrollEnd}
          overscan={5}
        />
      );
    }, [
      isFetching,
      hasNoResults,
      items,
      getItemDisabled,
      renderItem,
      onScrollEnd,
      className,
    ]);

    return (
      <VirtualMultiselect.Root
        className="flex flex-col gap-2"
        value={value}
        onValueChange={onValueChange}
      >
        {multiselectContent}
        {!isFetching && !hasNoResults && (
          <VirtualMultiselect.SelectAllButton
            id="core-general-filter-toggle-all"
            className="w-full"
          />
        )}
      </VirtualMultiselect.Root>
    );
  },
) as <Item extends { id: string }>(
  props: CoreGeneralFilterProps<Item> & {
    ref?: React.Ref<CoreGeneralFilterRef>;
  },
) => React.ReactElement | null;

const CoreGeneralFilterContentContainer = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center bg-white border rounded-sm shadow-sm',
        className,
      )}
    >
      {children}
    </div>
  );
};

const NoResultMessage = ({ className }: { className?: string }) => {
  const { common } = useAutoneTranslation();

  return (
    <CoreGeneralFilterContentContainer className={cn('p-8', className)}>
      <NoDataSvg height={120} width={120} />
      <p className="text-muted-foreground text-lg font-medium">
        {common('no-results')}
      </p>
    </CoreGeneralFilterContentContainer>
  );
};

const Loader = ({ className }: { className?: string }) => {
  const { common } = useAutoneTranslation();
  return (
    <CoreGeneralFilterContentContainer className={cn('p-8', className)}>
      <div className="flex gap-2 items-center">
        <LoadingIcon size={'base'} className="text-primary animate-spin" />
        <p className="text-muted-foreground">{common('loading')}</p>
      </div>
    </CoreGeneralFilterContentContainer>
  );
};
